import userLocalStorage from 'live/lib/user-local-storage'
import { ColorStyle, CustomSettingsByChannel, ViewMode } from './api'

// Live's jsonConfig is one canonical row shared by every viewer of a dashboard and the widget's own
// editor - there's no generic per-viewer split for a plugin's own config fields (the platform's only
// such split is hardcoded to its native Y-axis config, not reusable here). A dashboard *viewer's* own
// channel/view-mode customization is therefore kept browser-local instead, exactly the kind of
// ephemeral, non-canonical preference this codebase's own conventions call out as fair game for
// browser storage. It never touches the widget's canonical setup, and never affects other viewers.
//
// Stored via Live's own `userLocalStorage` (not raw `window.localStorage`) so this matches the same
// standard behavior as Live's native per-viewer widget overrides (e.g. the Y-axis config): every key
// is namespaced by the logged-in user's id (`user/<userId>/<key>`), not just by browser. That's what
// keeps two different Live accounts on the same machine from seeing or overwriting each other's
// preference, while still surviving a logout/login of the *same* user on the *same* browser - it's
// still browser-local storage underneath, just scoped to who's logged in rather than just the device.

export type ChannelsOverride = {
    selectedChannels: CustomSettingsByChannel[]
    excludedChannels: string[]
}

export type ViewerOverride = {
    channels?: ChannelsOverride
    viewMode?: ViewMode
    colorStyle?: ColorStyle
    colorTextEnabled?: boolean
}

const storageKey = (widgetId: string | number): string => `live-multi-values-widget:viewer-override:${widgetId}`

export const loadViewerOverride = (widgetId: string | number): ViewerOverride | null => {
    try {
        const raw = userLocalStorage.getItem(storageKey(widgetId))

        return raw ? (JSON.parse(raw) as ViewerOverride) : null
    } catch {
        return null // storage unavailable/corrupted - fall back to the canonical setup, same as never having customized anything
    }
}

export const saveViewerOverride = (widgetId: string | number, override: ViewerOverride): void => {
    try {
        userLocalStorage.setItem(storageKey(widgetId), JSON.stringify(override))
    } catch {
        // best-effort - a personal preference failing to persist shouldn't break the widget
    }
}

export const clearViewerOverride = (widgetId: string | number): void => {
    try {
        userLocalStorage.removeItem(storageKey(widgetId))
    } catch {
        // no-op
    }
}
